import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Ban, CalendarX, Check, Clock, Copy, CreditCard, LogIn, LogOut, Mail, MessageCircle, Phone, Printer, ShieldAlert } from "lucide-react";
import api, { formatINR, mediaUrl } from "../lib/api";
import { payForBooking } from "../lib/razorpay";
import { useContent } from "../context/SiteContentContext";
import { PAYMENT_LABEL, STATUS_LABEL, errorMessage, fmtDay, getBookingToken, saveBookingToken } from "../lib/stays";

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">{label}</div>
      <div className="text-white mt-1">{children}</div>
    </div>
  );
}

export default function BookingConfirmationPage() {
  const { id } = useParams();
  const { get: content } = useContent();
  const location = useLocation();
  const [booking, setBooking] = useState(location.state?.booking || null);
  const [state, setState] = useState(location.state?.booking ? "ready" : "loading");
  const [cancelling, setCancelling] = useState(false);
  const [paying, setPaying] = useState(false);
  const [now, setNow] = useState(Date.now());
  const token = location.state?.token || getBookingToken(id);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/bookings/${id}`, { headers: token ? { "X-Booking-Token": token } : {} });
      setBooking(data);
      setState("ready");
      if (token) saveBookingToken(id, token);
    } catch {
      setState("missing");
    }
  }, [id, token]);

  useEffect(() => { load(); }, [load]);

  const awaitingPayment = !!booking?.online_payment_due || !!booking?.qr_payment_due;
  // While payment is outstanding: tick the countdown and re-check the server (the webhook may confirm it).
  useEffect(() => {
    if (!awaitingPayment) return undefined;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    const poll = setInterval(load, 5000);
    return () => { clearInterval(tick); clearInterval(poll); };
  }, [awaitingPayment, load]);

  const copy = async (text, what) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${what} copied`); } catch { toast.error("Couldn't copy - please select and copy it manually"); }
  };

  const payNow = async () => {
    setPaying(true);
    const r = await payForBooking(id, token);
    if (r.status === "paid") toast.success("Payment received - your booking is confirmed");
    else if (r.status === "failed" && r.message) toast.error(r.message);
    await load();
    setPaying(false);
  };

  const cancel = async () => {
    const p = booking.cancellation_preview;
    const msg = p?.non_refundable
      ? "This booking is non-refundable. Cancelling will NOT give you a refund. Cancel anyway?"
      : `Cancel this booking? Refund per policy: ${p ? p.refund_percent : 0}%.`;
    if (!window.confirm(msg)) return;
    setCancelling(true);
    try {
      await api.post(`/bookings/${id}/cancel`, {}, { headers: token ? { "X-Booking-Token": token } : {} });
      toast.success("Your booking has been cancelled");
      await load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
    setCancelling(false);
  };

  if (state === "loading") {
    return <div className="pt-40 pb-24 min-h-screen text-center text-white/40" aria-busy="true" data-testid="confirmation-page">Loading your booking…</div>;
  }
  if (state === "missing" || !booking) {
    return (
      <div className="pt-40 pb-24 min-h-screen text-center px-6" data-testid="confirmation-page">
        <ShieldAlert className="mx-auto text-gold mb-4" />
        <h1 className="font-display text-3xl">We couldn't find that booking</h1>
        <p className="text-white/50 mt-3 max-w-md mx-auto">Open the link on the device you booked with, or sign in to see your reservations.</p>
        <Link to="/dashboard" className="btn-gold inline-block mt-8">My bookings</Link>
      </div>
    );
  }

  const b = booking;
  const guestsText = `${b.adults} adult${b.adults !== 1 ? "s" : ""}${b.children ? `, ${b.children} child${b.children !== 1 ? "ren" : ""}` : ""}${b.infants ? `, ${b.infants} infant${b.infants !== 1 ? "s" : ""}` : ""}${b.pets ? `, ${b.pets} pet${b.pets !== 1 ? "s" : ""}` : ""}`;
  const active = b.status === "pending" || b.status === "confirmed";
  const nonRefundable = b.refund_type === "NON_REFUNDABLE";
  const ok = b.status === "confirmed" || b.status === "checked_in" || b.status === "completed";
  const secondsLeft = b.expires_at ? Math.max(0, Math.floor((new Date(b.expires_at).getTime() - now) / 1000)) : 0;
  const qrDue = !!b.qr_payment_due;
  const holdActive = awaitingPayment && secondsLeft > 0;
  const ins = b.payment_instructions;
  const waText = ins ? `Hi, I have paid ${formatINR(ins.amount)} for booking ${b.reference} (${b.guest_name}, ${fmtDay(b.check_in_date, { day: "numeric", month: "short" })} to ${fmtDay(b.check_out_date, { day: "numeric", month: "short" })}). Payment screenshot attached.` : "";
  const waNumber = (ins?.whatsapp || content("site.whatsapp") || "").replace(/\D/g, "");
  const waLink = ins && waNumber ? `https://wa.me/${waNumber}?text=${encodeURIComponent(waText)}` : "";
  const holdExpired = b.status === "expired" || (awaitingPayment && secondsLeft === 0);
  const mmss = `${String(Math.floor(secondsLeft / 60)).padStart(2, "0")}:${String(secondsLeft % 60).padStart(2, "0")}`;

  return (
    <div className="pt-28 sm:pt-32 pb-24 grain min-h-screen print:pt-4" data-testid="confirmation-page">
      <div className="max-w-3xl mx-auto px-5 sm:px-6">
        <div className="text-center mb-10">
          <div className={`mx-auto w-16 h-16 border flex items-center justify-center mb-6 ${ok ? "border-green-400/50 text-green-400" : "border-gold/50 text-gold"} confirm-pop`}>
            {ok ? <Check size={28} /> : holdActive ? <CreditCard size={28} /> : <CalendarX size={28} />}
          </div>
          <div className="eyebrow">{ok ? "Booking Confirmed" : qrDue ? "Awaiting Payment" : holdActive ? "Payment Pending" : STATUS_LABEL[b.status] || b.status}</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-4" data-testid="booking-reference">{b.reference}</h1>
          <p className="text-white/50 text-sm mt-3">{qrDue && holdActive ? "Pay using the QR below and send us the screenshot on WhatsApp to confirm." : holdActive ? "Complete your payment to confirm this booking." : holdExpired ? "The payment window has closed." : b.status === "pending" ? "Your request is awaiting confirmation from the property." : `A confirmation has been sent to ${b.guest_email}.`}</p>
        </div>

        {qrDue && ins && (
          <section className="mb-6 glass p-6 sm:p-8 border border-gold/40" data-testid="qr-panel" aria-labelledby="qr-h">
            <h2 id="qr-h" className="font-display text-2xl mb-1 flex items-center gap-2"><CreditCard size={20} className="text-gold" /> Complete your payment</h2>
            {holdActive ? (
              <p className="text-sm text-white/60 mb-6 flex items-center gap-2"><Clock size={14} className="text-gold" /> Your dates are held for <b className="font-display text-lg text-gold tabular-nums">{mmss}</b></p>
            ) : (
              <p className="text-sm text-red-200 mb-6">The hold has ended. If you've already paid, send us the screenshot below and we'll confirm if the dates are still free.</p>
            )}
            <div className="grid md:grid-cols-[280px_1fr] gap-8 items-start">
              <div className="mx-auto">
                {ins.qr_image ? (
                  <img src={mediaUrl(ins.qr_image)} alt="Payment QR code" className="w-full max-w-[280px] h-auto border border-white/10" data-testid="qr-image" />
                ) : (
                  <div className="w-60 h-60 border border-white/10 flex items-center justify-center text-center text-xs text-white/40 p-4">QR code not available yet. Please pay using the UPI ID{ins.upi_id ? "" : " — contact us on WhatsApp for details"}.</div>
                )}
                <div className="text-center text-xs text-white/40 mt-2">Scan with any UPI app</div>
              </div>
              <div className="space-y-5">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Amount to pay</div>
                  <div className="font-display text-4xl text-gold mt-1" data-testid="qr-amount">{formatINR(ins.amount)}</div>
                </div>
                {ins.upi_id && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">UPI ID</div>
                    <button type="button" onClick={() => copy(ins.upi_id, "UPI ID")} className="mt-1 flex items-center gap-2 text-white hover:text-gold" data-testid="copy-upi"><span className="font-mono">{ins.upi_id}</span><Copy size={13} /></button>
                  </div>
                )}
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Booking reference (add in the payment note)</div>
                  <button type="button" onClick={() => copy(b.reference, "Reference")} className="mt-1 flex items-center gap-2 text-white hover:text-gold"><span className="font-mono">{b.reference}</span><Copy size={13} /></button>
                </div>
                <ol className="list-decimal pl-5 text-sm text-white/60 space-y-1">
                  <li>Pay exactly {formatINR(ins.amount)} using the QR or UPI ID.</li>
                  <li>Take a screenshot of the successful payment.</li>
                  <li>Send it to us on WhatsApp with your booking reference.</li>
                </ol>
                {waLink ? (
                  <a href={waLink} target="_blank" rel="noopener noreferrer" className="btn-gold inline-flex items-center gap-2" data-testid="whatsapp-send"><MessageCircle size={15} /> Send screenshot on WhatsApp</a>
                ) : (
                  <p className="text-sm text-white/50">WhatsApp number isn't set up yet - please contact us by phone or email.</p>
                )}
                <p className="text-xs text-white/40">Your booking is confirmed once we verify the payment. You'll get a confirmation after that.</p>
              </div>
            </div>
          </section>
        )}
        {holdActive && !qrDue && (
          <div className="mb-6 p-5 border border-gold/40 bg-gold/[0.06] flex flex-wrap items-center justify-between gap-4" data-testid="payment-banner">
            <div>
              <div className="flex items-center gap-2 text-white"><Clock size={15} className="text-gold" /> Your dates are held for <b className="font-display text-xl text-gold tabular-nums">{mmss}</b></div>
              <p className="text-xs text-white/50 mt-1">Pay {formatINR(b.total_amount)} now to confirm. If the timer runs out, the dates are released.</p>
            </div>
            <button type="button" onClick={payNow} disabled={paying} className="btn-gold disabled:opacity-50" data-testid="pay-now">{paying ? "Opening payment..." : `Pay ${formatINR(b.total_amount)}`}</button>
          </div>
        )}
        {holdExpired && !(b.payment_instructions) && (
          <div role="alert" className="mb-6 p-5 border border-red-500/40 bg-red-500/[0.05] text-sm text-red-100" data-testid="hold-expired">
            Your payment was not completed in time, so these dates were released. If money was deducted, it will be confirmed automatically or refunded to you. <Link to="/booking" className="text-gold underline">Start a new booking</Link>.
          </div>
        )}

        <div className="glass p-6 sm:p-8 space-y-7" data-testid="confirmation-details">
          <div className="grid sm:grid-cols-2 gap-6">
            <Field label="Property">{b.property.name}<div className="text-xs text-white/40 mt-1">{b.unit}</div></Field>
            <Field label="Guest">{b.guest_name}<div className="text-xs text-white/40 mt-1">{b.guest_email}</div></Field>
            <Field label="Check-in">
              <span className="flex items-center gap-2"><LogIn size={13} className="text-gold" />{fmtDay(b.check_in_date)}</span>
              <div className="text-xs text-white/50 mt-1">{b.booking_type === "hourly" ? b.check_in_time_label : `${b.check_in_time_label} onwards`}</div>
            </Field>
            <Field label="Check-out">
              <span className="flex items-center gap-2"><LogOut size={13} className="text-gold" />{fmtDay(b.check_out_date)}</span>
              <div className="text-xs text-white/50 mt-1">{b.booking_type === "hourly" ? b.check_out_time_label : `Before ${b.check_out_time_label}`}</div>
            </Field>
            <Field label="Guests">{guestsText}</Field>
            <Field label="Stay">{b.booking_type === "overnight" ? `${b.number_of_nights} night${b.number_of_nights !== 1 ? "s" : ""}` : "Hourly"}</Field>
          </div>

          <div className="border-t border-white/10 pt-6">
            <div className="space-y-2 text-sm">
              {b.pricing?.line_items?.map((l) => (
                <div key={l.key} className="flex justify-between"><span className="text-white/60">{l.label}</span>
                  <span>{l.amount.startsWith("-") ? "−" + formatINR(l.amount.slice(1)) : formatINR(l.amount)}</span></div>
              ))}
            </div>
            <div className="flex justify-between font-display text-2xl mt-4 pt-4 border-t border-white/10">
              <span>Total</span><span className="text-gold" data-testid="confirmation-total">{formatINR(b.total_amount)}</span>
            </div>
            <div className="text-xs text-white/40 mt-2">{PAYMENT_LABEL[b.payment_status] || b.payment_status}{b.payment_status === "not_required" && " — payable at the property"}</div>
          </div>

          <div className={`p-4 border ${nonRefundable ? "border-red-500/40 bg-red-500/[0.05]" : "border-white/10"}`} data-testid="confirmation-policy">
            <div className="flex items-center gap-2 text-white">{nonRefundable && <Ban size={14} className="text-red-400" />}<b>Booking policy: {nonRefundable ? "Non-refundable" : "Refundable"}</b></div>
            <ul className="list-disc pl-5 mt-2 text-sm text-white/60 space-y-1">
              {(b.policy_lines || []).map((l) => <li key={l}>{l}</li>)}
            </ul>
            {b.status === "cancelled" && b.refund_due !== "0.00" && <p className="text-sm text-gold mt-3">Refund due: {formatINR(b.refund_due)}</p>}
          </div>

          {b.special_requests && <Field label="Special requests"><span className="text-white/70 text-sm">{b.special_requests}</span></Field>}
        </div>

        <div className="flex flex-wrap gap-3 justify-center mt-8 print:hidden">
          <button type="button" onClick={() => window.print()} className="btn-ghost flex items-center gap-2"><Printer size={14} /> Print / save as PDF</button>
          <Link to="/dashboard" className="btn-ghost">View my bookings</Link>
          {b.contact?.phone && <a href={`tel:${b.contact.phone}`} className="btn-ghost flex items-center gap-2"><Phone size={14} /> Contact property</a>}
          {b.contact?.email && <a href={`mailto:${b.contact.email}`} className="btn-ghost flex items-center gap-2"><Mail size={14} /> Email</a>}
          {active && <button type="button" onClick={cancel} disabled={cancelling} className="btn-ghost text-red-300 border-red-500/30 disabled:opacity-40" data-testid="cancel-booking">{cancelling ? "Cancelling…" : "Cancel booking"}</button>}
        </div>
        <p className="text-center text-xs text-white/30 mt-8 print:hidden">Bring valid photo ID for every guest. Questions? <Link to="/rules" className="text-gold">Read the house rules</Link>.</p>
      </div>
    </div>
  );
}
